/**
 * Where every station sits, computed here and never sent by the platform.
 *
 * This file is the "structure belongs to us" law as code. The API publishes
 * facts about a subject and a campaign; which node is hot, where it sits, how
 * the route bends between two stations and which zone a station falls in are
 * all rendering decisions, and a platform value that had to move whenever this
 * repository redrew the map would be a wire field that is wrong at least once
 * (contract 0.3.0, pending adjustment 6).
 *
 * It also means route length is not a fixed thirteen. A customer with four
 * live campaigns and one with forty both get a route that reads, because the
 * world grows with the content rather than the art being cut to fit.
 */

import type { MeCampaign, SeasonTrack } from "../data/contract";

/** The world's coordinate space. The frame scrolls through it. */
export const WORLD_WIDTH = 780;

/** Vertical distance between two consecutive stations. */
const STEP = 108;
/** How far the route may wander from the centre line. */
const SWING = 196;
/** Clear space above the summit and below the first station. */
const TOP_MARGIN = 300;
const BOTTOM_MARGIN = 120;

export type StationKind = "quest" | "reward" | "gate";
/** The settled set: quest stations, reward markers, and gates in neither. */
export type QuestState = "todo" | "hot" | "done";
export type RewardState = "locked" | "earned";

export interface Station {
  id: string;
  kind: StationKind;
  x: number;
  y: number;
  /** Quest stations only. */
  quest?: {
    state: QuestState;
    campaign: MeCampaign;
    /** 0 to 1, meaningful while hot. */
    fraction: number;
  };
  /** Reward markers only. */
  marker?: {
    state: RewardState;
    label: string;
    assetKey: "coin" | "crystal";
    requirement: string | null;
    tier: number;
  };
  /** Gates only. */
  gate?: { label: string; requirement: string | null };
}

export interface Zone {
  name: string;
  top: number;
  height: number;
}

export interface MapModel {
  stations: Station[];
  zones: Zone[];
  summit: { x: number; y: number; label: string; earned: boolean } | null;
  worldHeight: number;
  /** Index into `stations` of the one hot station, or -1 when none is. */
  hotIndex: number;
  /** How many quest stations remain unfinished. */
  remaining: number;
}

/**
 * Where a station sits across the world's width.
 *
 * Deterministic on purpose: a route of the same length always draws the same
 * shape, because randomness here would move the map under a player between two
 * reads of one season, which reads as a bug even when nothing is wrong.
 *
 * The shape is one long sine crossing the centre roughly every five stations,
 * plus a small second harmonic so no two turns are identical. Strict left,
 * right, left alternation was tried and rejected: it reads as a machine
 * drawing a zigzag rather than a path finding its way uphill, and it puts a
 * hard turn between every pair of stations rather than letting the route run.
 */
function xAt(i: number): number {
  const centre = WORLD_WIDTH / 2;
  const primary = Math.sin(i * ((2 * Math.PI) / 5.4) + 0.6) * 0.86;
  const detail = Math.sin(i * 1.37 + 2.2) * 0.17;
  return Math.round(centre + SWING * (primary + detail));
}

/**
 * Decide each quest station's state from the platform's facts.
 *
 * Exactly one station is hot: the first unfinished one the player is enrolled
 * in, or failing that the first unfinished one at all, because a route with
 * nothing lit gives a returning player no answer to "what now". A season with
 * everything finished has no hot station, which is the summit moment.
 */
function questState(campaign: MeCampaign, isHot: boolean): QuestState {
  if (campaign.enrollment === "completed" || campaign.completedAt !== null) return "done";
  return isHot ? "hot" : "todo";
}

function rewardLabel(track: SeasonTrack["rewards"][number]): { label: string; assetKey: "coin" | "crystal" } {
  const { reward } = track;
  if (reward.kind === "credits" && typeof reward.value === "number") {
    return { label: `${reward.value.toLocaleString("en-US")} ${reward.currency ?? "coins"}`, assetKey: "coin" };
  }
  return { label: String(reward.value ?? "Reward"), assetKey: "crystal" };
}

export interface BuildOptions {
  campaigns: MeCampaign[];
  season: SeasonTrack | null | undefined;
  /** When false, coin rewards are not drawn: coins follow the store. */
  showCoins: boolean;
}

export function buildMap({ campaigns, season, showCoins }: BuildOptions): MapModel {
  // Reward markers are interleaved into the route at their tier's share of the
  // journey, so a tier reward sits between the quests that earn it rather than
  // in a separate column the eye has to reconcile with the path.
  const rewards = season?.rewards ?? [];
  const slots: { kind: StationKind; campaign?: MeCampaign; tierIndex?: number }[] = [];

  const perTier = rewards.length > 0 ? Math.ceil(campaigns.length / (rewards.length + 1)) : campaigns.length;
  let tier = 0;
  campaigns.forEach((campaign, i) => {
    slots.push({ kind: "quest", campaign });
    const atBoundary = perTier > 0 && (i + 1) % perTier === 0;
    if (atBoundary && tier < rewards.length) {
      slots.push({ kind: "reward", tierIndex: tier });
      // A gate stands in front of the last tier, which is the one that opens
      // the run to the summit. Nothing else in the route is gated today,
      // because the journey graph that would describe real gates is unbuilt.
      if (tier === rewards.length - 1) slots.push({ kind: "gate", tierIndex: tier });
      tier += 1;
    }
  });

  const drawn = slots.filter((s) => {
    if (s.kind !== "reward") return true;
    if (showCoins) return true;
    // Store off: a coin marker has nothing to spend on, so it is not drawn.
    // The tier itself still exists; only its coin face is withheld.
    const r = rewards[s.tierIndex ?? 0];
    return r ? !(r.reward.kind === "credits") : false;
  });

  const rewardCount = drawn.filter((s) => s.kind === "reward").length;
  const worldHeight =
    TOP_MARGIN + drawn.length * STEP + rewardCount * 164 + BOTTOM_MARGIN;

  // The first unfinished enrolled quest is hot; failing that the first
  // unfinished one at all. Computed over campaigns, not over drawn slots, so
  // hiding a coin marker can never move which quest is lit.
  const unfinished = campaigns.filter((c) => c.enrollment !== "completed" && c.completedAt === null);
  const hotCampaign =
    unfinished.find((c) => c.enrollment === "enrolled") ?? unfinished[0] ?? null;

  // Vertical positions are accumulated rather than multiplied, because a
  // reward marker is taller than a quest disc: it carries a rendered object
  // plus a two line caption, and a neighbour one uniform step away lands on
  // that caption. Markers claim the extra room they actually occupy.
  const ys: number[] = [];
  let cursor = worldHeight - BOTTOM_MARGIN;
  drawn.forEach((slot, i) => {
    if (i > 0) cursor -= slot.kind === "reward" || drawn[i - 1]?.kind === "reward" ? STEP + 82 : STEP;
    ys.push(cursor);
  });

  const stations: Station[] = drawn.map((slot, i) => {
    // Bottom of the world is the start, top is the summit.
    const y = ys[i] ?? worldHeight - BOTTOM_MARGIN;
    const x = xAt(i);

    if (slot.kind === "quest" && slot.campaign) {
      const campaign = slot.campaign;
      const isHot = hotCampaign !== null && campaign.id === hotCampaign.id;
      const target = campaign.goal.target || 1;
      return {
        id: campaign.id,
        kind: "quest",
        x,
        y,
        quest: {
          state: questState(campaign, isHot),
          campaign,
          fraction: Math.max(0, Math.min(1, campaign.goal.current / target)),
        },
      };
    }

    const track = rewards[slot.tierIndex ?? 0];
    if (slot.kind === "reward" && track) {
      const { label, assetKey } = rewardLabel(track);
      return {
        id: `tier_${track.tier}`,
        kind: "reward",
        x,
        y,
        marker: {
          state: track.earned ? "earned" : "locked",
          label,
          assetKey,
          requirement: track.earned ? null : track.requirement,
          tier: track.tier,
        },
      };
    }

    return {
      id: `gate_${slot.tierIndex ?? 0}`,
      kind: "gate",
      x,
      y,
      gate: {
        label: "Broadcast gate",
        requirement: track ? `Reach tier ${track.tier}` : null,
      },
    };
  });

  const hotIndex = stations.findIndex((s) => s.quest?.state === "hot");
  const remaining = stations.filter((s) => s.quest && s.quest.state !== "done").length;

  // Zones divide the climb into thirds and are named for the season's own
  // arc. They are decoration with a job: a player scrolling a long route needs
  // to know roughly where they are without counting stations.
  const names = ["First light", "Relay", "Broadcast"];
  const band = (worldHeight - TOP_MARGIN + 120) / 3;
  const zones: Zone[] = names.map((name, i) => ({
    name,
    top: TOP_MARGIN - 120 + (2 - i) * band,
    height: band,
  }));

  const summitEarned = season?.summit?.earned ?? false;
  const summit = season?.summit
    ? {
        x: Math.round(WORLD_WIDTH / 2),
        y: 180,
        label: String(season.summit.reward.value ?? "Season reward"),
        earned: summitEarned,
      }
    : null;

  return { stations, zones, summit, worldHeight, hotIndex, remaining };
}

/**
 * A Catmull Rom spline through the station centres, rendered as cubic beziers.
 * The route is the one solid line on the screen, so it gets the smooth curve;
 * everything else is straight or dotted.
 */
export function routePath(points: [number, number][]): string {
  if (points.length === 0) return "";
  const first = points[0]!;
  let d = `M${first[0]},${first[1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[Math.min(points.length - 1, i + 2)]!;
    d +=
      `C${p1[0] + (p2[0] - p0[0]) / 6},${p1[1] + (p2[1] - p0[1]) / 6} ` +
      `${p2[0] - (p3[0] - p1[0]) / 6},${p2[1] - (p3[1] - p1[1]) / 6} ` +
      `${p2[0]},${p2[1]}`;
  }
  return d;
}
